import React, { memo, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Outlet, useLocation } from "react-router-dom";
import styles from "./UtilityLayout.module.scss";
import { RootState } from "../../stores/store";
import { ApplicationRoutes } from "../../routes";
import { SubMenuData, SubMenuInteraction } from "../menu.types";
import { MENU_STRUCTURE } from "./UtilityMenu.constant";
import { useConfigLayout } from "../../hooks/useConfigLayout";
import UtilitySideMenu from "./UtilitySideMenu";
import UtilityHeader from "./UtilityHeader";

type Props = {};

const UtilityLayout: React.FC<Props> = memo((props: Props) => {
	const [isColapsed, setIsColapsed] = useState(false);
	const [isDisabled, setIsDisabled] = useState(false);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

	const [currentSubMenu, setCurrentSubMenu] = useState<SubMenuInteraction | null>(null);

	useConfigLayout({
		showCountrySelector: true,
		utility: null,
		showExportButton: false,
		showBreadCrumb: true,
		exportHandler: () => {},
	});

	const location = useLocation();
	const firstLogin = useSelector((state: RootState) => state.firstLogin.firstLogin);

	const isMobileOptimized = location.pathname.includes(ApplicationRoutes.CUSTOMER_SUPPORT);

	const getActiveSubMenuFromUrl = (currentPath: string) => {
		let menuItems;

		// Detect module from URL and load appropriate menu
		switch (true) {
			case currentPath.includes(`${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.BILLS_OPERATIONS}`):
				menuItems = MENU_STRUCTURE.billsOperationsMenu;
				break;
			case currentPath.includes(`${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.SERVICE_REQUEST_MODULE}`):
				menuItems = MENU_STRUCTURE.serviceRequestMenu;
				break;
			case currentPath.includes(`${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.CUSTOMER_SUPPORT}`):
				menuItems = MENU_STRUCTURE.customerSupportMenu;
				break;
			case currentPath.includes(`${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.CLIENT_MANAGEMENT}`):
				menuItems = MENU_STRUCTURE.clientManagementMenu;
				break;
			default:
				return null;
		}

		// Find the active submenu item
		for (const menuItem of menuItems) {
			// Check if any submenu item matches the current path
			if (menuItem.subMenuItems && menuItem.subMenuItems.length > 0) {
				const activeSubMenuItem = menuItem.subMenuItems.find(subItem => currentPath === subItem.route);

				if (activeSubMenuItem) {
					return {
						mainMenuName: menuItem.text,
						subMenuItems: menuItem.subMenuItems,
						isOpen: true,
						activeSubMenuItem: activeSubMenuItem.text,
					};
				}
			}
		}

		return null;
	};

	function toggleColapsed() {
		setIsColapsed(!isColapsed);
	}

	function toggleMobileMenu() {
		setIsMobileMenuOpen(!isMobileMenuOpen);
	}

	const handleSubMenuInteraction = (mainMenuName: string, subMenuItems: SubMenuData[], isOpen: boolean, activeSubMenuItem?: string) => {
		const subMenuData: SubMenuInteraction = {
			mainMenuName,
			subMenuItems,
			isOpen,
			activeSubMenuItem,
		};

		setCurrentSubMenu(subMenuData);
	};

	useEffect(() => {
		const activeSubMenu = getActiveSubMenuFromUrl(location.pathname);
		if (activeSubMenu) {
			setCurrentSubMenu(activeSubMenu);
		} else {
			// If no submenu should be active, close all submenus
			setCurrentSubMenu(null);
		}
	}, [location.pathname]);

	useEffect(() => {
		if (currentSubMenu) {
			// Do something when submenu state changes
			console.log("Current submenu changed:", currentSubMenu);
		}
	}, [currentSubMenu]);

	useEffect(() => {
		setIsDisabled(firstLogin);
	}, [firstLogin]);

	return (
		<div className={styles.container}>
			<UtilitySideMenu
				onSubMenuInteraction={handleSubMenuInteraction}
				mobileMenuOpen={isMobileMenuOpen}
				onMobileMenuClick={toggleMobileMenu}
				isColapsed={false}
				isDisabled={false}
			/>

			<div className={styles.contentContainer}>
				<UtilityHeader onMobileMenuClick={toggleMobileMenu} />
				<div className={`${styles.content} ${isColapsed ? styles.contentWidthCollapsedSideMenu : styles.contentWidthExpandedSideMenu}`}>
					{!isMobileOptimized && (
						<div className={styles.desktopNotice}>
							<span className={styles.desktopNoticeIcon}>🖥️</span>
							<span className={styles.desktopNoticeTitle}>Desktop only</span>
							<span className={styles.desktopNoticeText}>
								This page is optimized for desktop devices. Please use a desktop browser for the best experience.
							</span>
						</div>
					)}
					<Outlet />
				</div>
			</div>
		</div>
	);
});

export default UtilityLayout;
